import { ArrowLeft, Moon, Heart, Activity } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";

const RecoveryScoreDetail = () => {
  const navigate = useNavigate();

  const weeklyData = [
    { day: "Mon", recovery: 72, sleep: 7.5, hrv: 62 },
    { day: "Tue", recovery: 68, sleep: 6.8, hrv: 58 },
    { day: "Wed", recovery: 82, sleep: 8.2, hrv: 71 },
    { day: "Thu", recovery: 78, sleep: 7.8, hrv: 68 },
    { day: "Fri", recovery: 75, sleep: 7.2, hrv: 65 },
    { day: "Sat", recovery: 88, sleep: 9.0, hrv: 75 },
    { day: "Sun", recovery: 85, sleep: 8.5, hrv: 72 },
  ];

  const monthlyData = [
    { week: "Week 1", recovery: 72 },
    { week: "Week 2", recovery: 76 },
    { week: "Week 3", recovery: 80 },
    { week: "Week 4", recovery: 78 },
  ];

  const sleepBreakdown = [
    { stage: "Deep Sleep", hours: 1.8, color: "bg-chart-1" },
    { stage: "REM Sleep", hours: 2.2, color: "bg-chart-2" },
    { stage: "Light Sleep", hours: 3.5, color: "bg-chart-3" },
    { stage: "Awake", hours: 0.3, color: "bg-muted" },
  ];

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/home")}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">Recovery Score</h1>
          <div className="w-10" />
        </div>

        {/* Today's Stats */}
        <Card className="from-chart-2/10 to-chart-2/5 rounded-2xl border-0 bg-gradient-to-br p-6 shadow-sm">
          <div className="text-center">
            <p className="mb-2 text-sm text-muted-foreground">
              Today's Recovery
            </p>
            <div className="mb-2 text-5xl font-bold text-foreground">78</div>
            <p className="text-sm text-muted-foreground">Well Recovered</p>
            <div className="mt-4 flex items-center justify-center gap-2">
              <span className="text-xs text-muted-foreground">Poor</span>
              <div className="via-warning to-success h-2 w-48 rounded-full bg-gradient-to-r from-destructive">
                <div className="relative h-full w-full">
                  <div className="absolute left-[78%] top-1/2 h-3 w-3 -translate-y-1/2 rounded-full border-2 border-background bg-foreground" />
                </div>
              </div>
              <span className="text-xs text-muted-foreground">Excellent</span>
            </div>
          </div>
        </Card>

        {/* Recovery Components */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-4 font-bold text-foreground">
            Recovery Components
          </h3>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-chart-2/10 rounded-xl p-3 text-center">
              <Moon className="text-chart-2 mx-auto mb-2 h-6 w-6" />
              <p className="text-2xl font-bold text-foreground">7.8h</p>
              <p className="text-xs text-muted-foreground">Sleep</p>
            </div>
            <div className="bg-chart-1/10 rounded-xl p-3 text-center">
              <Heart className="text-chart-1 mx-auto mb-2 h-6 w-6" />
              <p className="text-2xl font-bold text-foreground">68</p>
              <p className="text-xs text-muted-foreground">HRV (ms)</p>
            </div>
            <div className="bg-chart-3/10 rounded-xl p-3 text-center">
              <Activity className="text-chart-3 mx-auto mb-2 h-6 w-6" />
              <p className="text-2xl font-bold text-foreground">52</p>
              <p className="text-xs text-muted-foreground">Resting HR</p>
            </div>
          </div>
        </Card>

        {/* Sleep Breakdown */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-4 font-bold text-foreground">Sleep Breakdown</h3>
          <div className="space-y-3">
            {sleepBreakdown.map((stage, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className={`h-3 w-3 rounded-full ${stage.color}`} />
                <div className="flex-1">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">
                      {stage.stage}
                    </span>
                    <span className="text-sm font-bold text-foreground">
                      {stage.hours}h
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-muted/30">
                    <div
                      className={`h-full ${stage.color} rounded-full`}
                      style={{ width: `${(stage.hours / 7.8) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* What is Recovery Score */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">
            What is Recovery Score?
          </h3>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Your Recovery Score combines sleep quality, heart rate variability
            (HRV), and resting heart rate to measure how well your body has
            recovered overnight. A higher score indicates better readiness for
            physical activity and stress management. Sleep duration and quality
            are the primary factors.
          </p>
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Excellent Recovery</span>
              <span className="text-success font-semibold">85-100</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Good Recovery</span>
              <span className="font-semibold text-primary">70-84</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Fair Recovery</span>
              <span className="text-warning font-semibold">50-69</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Poor Recovery</span>
              <span className="font-semibold text-destructive">&lt;50</span>
            </div>
          </div>
        </Card>

        {/* Charts */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <Tabs defaultValue="weekly" className="w-full">
            <TabsList className="mb-4 grid w-full grid-cols-2">
              <TabsTrigger value="weekly">Weekly</TabsTrigger>
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
            </TabsList>

            <TabsContent value="weekly" className="space-y-4">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={weeklyData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="hsl(var(--border))"
                    />
                    <XAxis
                      dataKey="day"
                      stroke="hsl(var(--muted-foreground))"
                      style={{ fontSize: "12px" }}
                    />
                    <YAxis
                      stroke="hsl(var(--muted-foreground))"
                      style={{ fontSize: "12px" }}
                      domain={[0, 100]}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                      }}
                    />
                    <Bar
                      dataKey="recovery"
                      fill="hsl(var(--chart-2))"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>

            <TabsContent value="monthly" className="space-y-4">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthlyData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="hsl(var(--border))"
                    />
                    <XAxis
                      dataKey="week"
                      stroke="hsl(var(--muted-foreground))"
                      style={{ fontSize: "12px" }}
                    />
                    <YAxis
                      stroke="hsl(var(--muted-foreground))"
                      style={{ fontSize: "12px" }}
                      domain={[0, 100]}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="recovery"
                      stroke="hsl(var(--chart-2))"
                      strokeWidth={2}
                      dot={{ fill: "hsl(var(--chart-2))", r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>
          </Tabs>
        </Card>

        {/* Key Insights */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">Key Insights</h3>
          <div className="space-y-3">
            <div className="flex gap-3">
              <div className="bg-success mt-2 h-2 w-2 rounded-full" />
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">
                  Your recovery peaks on weekends with longer sleep duration
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="mt-2 h-2 w-2 rounded-full bg-primary" />
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">
                  HRV has increased by 12% over the past month with consistent
                  sleep
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="bg-warning mt-2 h-2 w-2 rounded-full" />
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">
                  Consider avoiding screens 1 hour before bed to improve deep
                  sleep
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default RecoveryScoreDetail;
